import {useContext, useCallback} from 'react';
import {VTContext} from '../index';

export function useLoggedIn() {
    const [context, updateContext] = useContext(VTContext);
    const isLoggedIn = context.isLoggedIn;
    const setLoggedIn = useCallback(function (loggedIn) {
        updateContext(Object.assign({}, context, {
            isLoggedIn: loggedIn
        }));
        if (!loggedIn) {
            sessionStorage.clear();
        } else {
            sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
                isLoggedIn: loggedIn
            })));
        }
    }, [context, updateContext]);
    return [isLoggedIn, setLoggedIn];
}

export function useCurrentUser() {
    const [context, updateContext] = useContext(VTContext);
    const currentUser = context.currentUser;
    const setCurrentUser = useCallback(function (currentUser) {
        updateContext(Object.assign({}, context, {
            isLoggedIn: true,
            currentUser: currentUser,
            adminUser: currentUser.isAdmin || currentUser.isCitepayAdmin,
            citepayAdminUser: currentUser.isCitepayAdmin
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            isLoggedIn: true,
            currentUser: currentUser,
            adminUser: currentUser.isAdmin || currentUser.isCitepayAdmin,
            citepayAdminUser: currentUser.isCitepayAdmin
        })));
    }, [context, updateContext]);
    return [currentUser, setCurrentUser];
}

export function useAuthentication() {
    const [context, updateContext] = useContext(VTContext);
    const setAuthentication = useCallback(function (loginData) {
        updateContext(Object.assign({}, context, {
            isLoggedIn: true,
            authToken: loginData.authToken,
            currentUser: loginData.currentUser,
            citepayAdminUser: loginData.currentUser.isCitepayAdmin,
            isNearPasswordExpiration: loginData.isNearPasswordExpiration,
            adminUser: loginData.currentUser.isAdmin || loginData.currentUser.isCitepayAdmin
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            isLoggedIn: true,
            authToken: loginData.authToken,
            currentUser: loginData.currentUser,
            citepayAdminUser: loginData.currentUser.isCitepayAdmin,
            isNearPasswordExpiration: loginData.isNearPasswordExpiration,
            adminUser: loginData.currentUser.isAdmin || loginData.currentUser.isCitepayAdmin
        })));
    }, [context, updateContext]);
    return [context, setAuthentication];
}

export function useNearPasswordExpiration() {
    const [context,] = useContext(VTContext);
    return context.currentUser.isNearPasswordExpiration;
}

export function useAdminUser() {
    const [context,] = useContext(VTContext);
    return context.adminUser;
}

export function useCitepayUser() {
    const [context,] = useContext(VTContext);
    return context.citepayAdminUser;
}

export function useRequestUrl() {
    const [context, updateContext] = useContext(VTContext);
    const url = context.requestUrl;
    const setRequestUrl = useCallback(function (requestUrl) {
        updateContext(Object.assign({}, context, {
            requestUrl: requestUrl
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            requestUrl: requestUrl
        })));
    }, [context, updateContext]);
    return [url, setRequestUrl];
}

export function useAuthToken() {
    const [context, updateContext] = useContext(VTContext);
    const authToken = context.authToken;
    const setAuthToken = useCallback(function (authToken) {
        updateContext(Object.assign({}, context, {
            authToken: authToken
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            authToken: authToken
        })));
    }, [context, updateContext]);
    return [authToken, setAuthToken];
}

export function useAgencyCourt() {
    const [context, updateContext] = useContext(VTContext);
    const currentCourt = context.agencyCourt;
    const setAgencyCourt = useCallback(function (court) {
        if (court === null) {
            delete context["agencyCourt"]
            updateContext(context);
            sessionStorage.setItem('VTContext', JSON.stringify(context));
        } else {
            updateContext(Object.assign({}, context, { agencyCourt: court }));
            sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
                agencyCourt: court
            })));
        }
    }, [context, updateContext]);
    return [currentCourt, setAgencyCourt];
}

export function useAgencies() {
    const [context, updateContext] = useContext(VTContext);
    const agencies = context.agencies
    const setAgencies = useCallback(function (agencies) {
        updateContext(Object.assign({}, context, {
            agencies: agencies
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            agencies: agencies
        })));
    }, [context, updateContext]);
    return [agencies, setAgencies];
}

export function useDefaultAgency() {
    const [context, updateContext] = useContext(VTContext);
    const defaultAgency = context.defaultAgency
    const setDefaultAgency = useCallback(function (defaultAgency) {
        updateContext(Object.assign({}, context, {
            defaultAgency: defaultAgency
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            defaultAgency: defaultAgency
        })));
    }, [context, updateContext]);
    return [defaultAgency, setDefaultAgency];
}

export function useCourtsByAgency() {
    const [context, updateContext] = useContext(VTContext);
    const courtsByAgency = context.courtsByAgency;
    const setCourtsByAgency = useCallback(function (courtsByAgency) {
        updateContext(Object.assign({}, context, {
            courtsByAgency: courtsByAgency
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            courtsByAgency: courtsByAgency
        })));
    }, [context, updateContext]);
    return [courtsByAgency, setCourtsByAgency];
}

export function useShowPasswordDialog() {
    const [context, updateContext] = useContext(VTContext)
    const showPasswordDialog = context.showPasswordDialog
    const setShowPasswordDialog = useCallback(function (showPasswordDialog) {
        updateContext(Object.assign({}, context, {
            showPasswordDialog: showPasswordDialog
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            showPasswordDialog: showPasswordDialog
        })));
    }, [context, updateContext]);
    return [showPasswordDialog, setShowPasswordDialog];
}

export function usePasswordResetToken() {
    const [context, updateContext] = useContext(VTContext)
    const passwordResetToken = context.passwordResetToken
    const setPasswordResetToken = useCallback(function (token) {
        updateContext(Object.assign({}, context, {
            passwordResetToken: token
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            passwordResetToken: token
        })));
    }, [context, updateContext])
    return [passwordResetToken, setPasswordResetToken]
}

export function useFullCourtData() {
    const [context, updateContext] = useContext(VTContext)
    const fullCourtData = context.fullCourtData
    const setFullCourtData = useCallback(function (full_court_data) {
        if (full_court_data === null) {
            delete context["fullCourtData"]
            updateContext(context);
            sessionStorage.setItem('VTContext', JSON.stringify(context));
        } else {
            updateContext(Object.assign({}, context, { fullCourtData: full_court_data }));
            sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
                fullCourtData: full_court_data
            })));
        }
    }, [context, updateContext])
    return [fullCourtData, setFullCourtData]
}

export function useApp() {
    const [context, updateContext] = useContext(VTContext);
    const app = context.app;
    const setApp = useCallback(function (appIn) {
        updateContext(Object.assign({}, context, {
            app: appIn
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            app: appIn
        })));
    }, [context, updateContext]);
    return [app, setApp];
}

export function useLogEntry() {
    const [context, updateContext] = useContext(VTContext);
    const logEntry = context.logEntry;
    const setLogEntry = useCallback(function (entryIn) {
        updateContext(Object.assign({}, context, {
            logEntry: entryIn
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            logEntry: entryIn
        })));
    }, [context, updateContext]);
    return [logEntry, setLogEntry];
}

export function useTerminals() {
    const [context, updateContext] = useContext(VTContext);
    const terminals = context.terminals;
    const setTerminals = useCallback(function (terms) {
        updateContext(Object.assign({}, context, {
            terminals: terms
        }));
        sessionStorage.setItem('VTContext', JSON.stringify(Object.assign({}, context, {
            terminals: terms
        })));
    }, [context, updateContext]);
    return [terminals, setTerminals];
}
